import React, {
    ForwardedRef,
    ReactNode,
    RefObject,
    useCallback,
    useMemo,
    useRef,
} from 'react';

import {
    Animated,
    Platform,
    Pressable,
    StyleProp,
    View,
    ViewStyle,
} from 'react-native';

import { ContentType } from 'services/data-manager/types';

import { isWeb } from 'utils/responsiveness';

import styles, {
    RibbonTouchableType,
    collectionContainerDimensions,
    collectionDimensions,
    collectionMargins,
    metadataTypeToRibbonType,
    stripeContainerDimensions,
    stripeDimensions,
    stripeMargins,
} from './styles';

type Props = {
    metadataType: ContentType;
    isCollection?: boolean;
    disabled?: boolean;
    style?: StyleProp<ViewStyle>;
    contentComponentStyle?: StyleProp<ViewStyle>;
    RibbonContentComponent?: ReactNode;
    RibbonInfoComponent?: ReactNode;
    onPress?(): void;
    testID?: string;
    railId?: string;
    accessibilityLabel?: string;
};

const metadataTypesWithoutClass = [
    ContentType.CastPerson,
    ContentType.ChannelItem,
    ContentType.Gallery,
];

function RibbonTouchable(
    {
        metadataType,
        style,
        disabled,
        contentComponentStyle,
        RibbonContentComponent,
        RibbonInfoComponent,
        isCollection = false,
        onPress,
        testID,
        railId,
        accessibilityLabel,
    }: Props,
    forwardedRef?: ForwardedRef<View | undefined>,
) {
    const ribbonTouchableRef = forwardedRef as RefObject<View> | undefined;
    const ribbonType =
        metadataTypeToRibbonType[metadataType] || RibbonTouchableType.LANDSCAPE;
    const animatedOpacity = useRef(new Animated.Value(0));
    const animatedStyle = useMemo(
        () => ({ opacity: animatedOpacity.current }),
        [animatedOpacity],
    );
    const dataClass = useMemo(() => {
        if (
            disabled ||
            metadataTypesWithoutClass.some((type) => type === metadataType)
        ) {
            return '';
        }

        return 'ribbonItemContent';
    }, [disabled, metadataType]);

    const animateInAndOut = useCallback(() => {
        Animated.sequence([
            Animated.timing(animatedOpacity.current, {
                toValue: 0.5,
                duration: 250,
                useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.delay(50),
            Animated.timing(animatedOpacity.current, {
                toValue: 0,
                duration: 200,
                useNativeDriver: Platform.OS !== 'web',
            }),
        ]).start();
    }, []);

    return (
        <Pressable
            ref={ribbonTouchableRef}
            testID={testID}
            style={[
                styles.container,
                isCollection
                    ? [
                          collectionContainerDimensions[ribbonType],
                          collectionMargins[metadataType],
                      ]
                    : [
                          stripeContainerDimensions[ribbonType],
                          stripeMargins[metadataType],
                      ],
                style,
            ]}
            disabled={disabled}
            delayLongPress={200}
            onPress={onPress}
            onLongPress={animateInAndOut}
            dataSet={{ class: dataClass, id: railId || '' }}
            accessibilityLabel={accessibilityLabel}
            role="button"
            tabIndex={
                isWeb && metadataType === ContentType.ChannelItem ? -1 : 0
            }
        >
            <View
                style={[
                    styles.contentContainer,
                    isCollection
                        ? collectionDimensions[ribbonType]
                        : stripeDimensions[ribbonType],
                    contentComponentStyle,
                ]}
                dataSet={{ hasBorder: `${!!dataClass}` }}
            >
                {RibbonContentComponent}
            </View>
            {RibbonInfoComponent}
            <Animated.View style={[styles.overlay, animatedStyle]} />
        </Pressable>
    );
}

export default React.forwardRef(RibbonTouchable);
